import { Check, ChevronDown, ChevronRight, Copy, ExternalLink, Search } from 'lucide-react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { cn } from '../../../lib/utils.js';
import type { CustomFieldsItem, PlanningObjectItem, WorkfrontObjectItem } from '../hooks.js';

/**
 * Per-scenario expand panel rendered under Administrator + Developer
 * rows. Mirrors `wf-audit/src/slices/scenario/components/
 * scenarioModal/WorkfrontIdTable.tsx` — single table, unified columns
 * (chevron · ID · Name · Module Name · Module #), with collapsible
 * group headers per Workfront object type. Custom fields land as a
 * trailing group with the same column shape.
 *
 * Grouping:
 *   - Workfront objects with `workfrontTypeLabel` group under that
 *     label (e.g., `Project`, `Task`, `Custom Form`).
 *   - Objects without a typeLabel (Workfront's `/object` lookup
 *     returned nothing) group under `Unidentified or Deleted ID` —
 *     same bucket the legacy used.
 *   - Custom fields land under their own `Custom Fields` group.
 *   - Planning objects (Maestro `Ws…`/`Rt…`/`F…`/`Rc…` references
 *     extracted from blueprints) land under a `Planning Objects`
 *     group. This is net-new vs the legacy `wf-audit` panel — the
 *     legacy didn't surface planning references per-scenario.
 */
export interface ScenarioExpandPanelProps {
  scenarioUrl: string | null | undefined;
  workfrontObjects: ReadonlyArray<WorkfrontObjectItem>;
  customFields: CustomFieldsItem | undefined;
  planningObjects: ReadonlyArray<PlanningObjectItem>;
  /**
   * Called when the user clicks the per-row "search by ID" button. The
   * page-level search input is set to this value so the scenario list
   * filters to scenarios containing this object. Optional — when
   * omitted (e.g. rendered outside the list page), the search button
   * is hidden.
   */
  onSearch?: (query: string) => void;
}

interface CustomFieldEntry {
  customField: string;
  modules: ReadonlyArray<{
    fusionModuleId: number | string;
    fusionModuleName: string;
    fusionModuleType?: string;
  }>;
}

const UNIDENTIFIED = 'Unidentified or Deleted ID';
const CUSTOM_FIELDS = 'Custom Fields';
const PLANNING_OBJECTS = 'Planning Objects';

// Maestro `planningObjectType` is one of four single-letter codes:
// `Ws` (Workspace), `Rt` (Record Type), `F` (Field), `Rc` (Record).
// We map to human labels for the chevron sub-group titles.
const PLANNING_TYPE_LABEL: Record<string, string> = {
  Ws: 'Workspace',
  Rt: 'Record Type',
  F: 'Field',
  Rc: 'Record',
};

export function ScenarioExpandPanel({
  scenarioUrl,
  workfrontObjects,
  customFields,
  planningObjects,
  onSearch,
}: ScenarioExpandPanelProps): JSX.Element {
  const wfGroups = useMemo(() => groupWorkfrontObjects(workfrontObjects), [workfrontObjects]);
  const cfEntries = useMemo(() => extractCustomFields(customFields), [customFields]);
  const planningTypeGroups = useMemo(() => groupPlanningByType(planningObjects), [planningObjects]);
  const groupKeys = useMemo(() => {
    const keys = wfGroups.map((g) => g.title);
    if (cfEntries.length > 0) keys.push(CUSTOM_FIELDS);
    if (planningTypeGroups.length > 0) keys.push(PLANNING_OBJECTS);
    return keys;
  }, [wfGroups, cfEntries.length, planningTypeGroups.length]);

  const [open, setOpen] = useState<Set<string>>(() => new Set(groupKeys));
  const toggle = (key: string): void => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Local `copiedKey` ticks for ~2s after a copy click so the icon
  // flips to a check — mirrors the legacy affordance. Single state
  // (vs per-row) keeps the implementation small; only one row's icon
  // is in the "copied" state at a time, which is what the user reads.
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const handleCopy = useCallback((value: string | number): void => {
    const str = String(value);
    void navigator.clipboard?.writeText(str);
    setCopiedKey(str);
    setTimeout(() => {
      setCopiedKey((prev) => (prev === str ? null : prev));
    }, 2000);
  }, []);

  if (wfGroups.length === 0 && cfEntries.length === 0 && planningTypeGroups.length === 0) {
    return (
      <div className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4 text-center text-sm text-foreground/50">
        No detected Workfront objects, custom fields, or planning objects for this scenario.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-foreground/10 bg-foreground/[0.02]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-foreground/10 text-xs uppercase tracking-wider text-foreground/50">
            <th className="w-8 p-2" />
            <th className="px-3 py-2 text-left font-medium">ID</th>
            <th className="px-3 py-2 text-left font-medium">Name</th>
            <th className="px-3 py-2 text-left font-medium">Module name</th>
            <th className="px-3 py-2 text-right font-medium">Module #</th>
          </tr>
        </thead>
        <tbody>
          {wfGroups.map((group) => (
            <Fragment key={group.title}>
              <GroupHeader
                title={group.title}
                count={group.items.length}
                expanded={open.has(group.title)}
                onToggle={() => toggle(group.title)}
              />
              {open.has(group.title)
                ? group.items.map((item) => (
                    <WorkfrontRow
                      key={item.id}
                      item={item}
                      scenarioUrl={scenarioUrl}
                      onSearch={onSearch}
                      onCopy={handleCopy}
                      copiedKey={copiedKey}
                    />
                  ))
                : null}
            </Fragment>
          ))}
          {cfEntries.length > 0 ? (
            <Fragment key={CUSTOM_FIELDS}>
              <GroupHeader
                title={CUSTOM_FIELDS}
                count={cfEntries.length}
                expanded={open.has(CUSTOM_FIELDS)}
                onToggle={() => toggle(CUSTOM_FIELDS)}
              />
              {open.has(CUSTOM_FIELDS)
                ? cfEntries.map((entry, i) => (
                    <CustomFieldRow
                      key={`cf-${i}-${entry.customField}`}
                      entry={entry}
                      scenarioUrl={scenarioUrl}
                      onSearch={onSearch}
                      onCopy={handleCopy}
                      copiedKey={copiedKey}
                    />
                  ))
                : null}
            </Fragment>
          ) : null}
          {planningTypeGroups.length > 0 ? (
            <Fragment key={PLANNING_OBJECTS}>
              <GroupHeader
                title={PLANNING_OBJECTS}
                count={planningTypeGroups.reduce((n, g) => n + g.items.length, 0)}
                expanded={open.has(PLANNING_OBJECTS)}
                onToggle={() => toggle(PLANNING_OBJECTS)}
              />
              {open.has(PLANNING_OBJECTS)
                ? planningTypeGroups.flatMap((group) => [
                    <SubGroupHeader
                      key={`pl-header-${group.type}`}
                      title={PLANNING_TYPE_LABEL[group.type] ?? group.type}
                      count={group.items.length}
                    />,
                    ...group.items.map((item, i) => (
                      <PlanningRow
                        key={`pl-${group.type}-${i}-${item.planningObjectId}-${item.fusionModuleId ?? 'nomod'}`}
                        item={item}
                        scenarioUrl={scenarioUrl}
                        onSearch={onSearch}
                        onCopy={handleCopy}
                        copiedKey={copiedKey}
                      />
                    )),
                  ])
                : null}
            </Fragment>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Non-collapsible sub-header rendered inside an open Planning Objects
 * group — one per Maestro record-kind (Workspace / Record Type / Field
 * / Record). Lighter visual weight than {@link GroupHeader} so the
 * eye reads it as a column-spanning label, not a separate group.
 */
function SubGroupHeader({ title, count }: { title: string; count: number }): JSX.Element {
  return (
    <tr className="border-b border-foreground/5">
      <td className="p-2" />
      <td colSpan={4} className="px-3 py-1.5 text-xs uppercase tracking-wider text-foreground/40">
        {title} ({count})
      </td>
    </tr>
  );
}

function GroupHeader({
  title,
  count,
  expanded,
  onToggle,
}: {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <tr
      onClick={onToggle}
      className="cursor-pointer border-b border-foreground/10 hover:bg-foreground/[0.04]"
    >
      <td colSpan={5} className="p-2">
        <div className="flex items-center gap-3">
          <span className="text-foreground/50">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
          <span className="text-sm font-medium text-foreground/90">
            {title} ({count})
          </span>
        </div>
      </td>
    </tr>
  );
}

function WorkfrontRow({
  item,
  scenarioUrl,
  onSearch,
  onCopy,
  copiedKey,
}: {
  item: WorkfrontObjectItem;
  scenarioUrl: string | null | undefined;
  onSearch: ((q: string) => void) | undefined;
  onCopy: (v: string | number) => void;
  copiedKey: string | null;
}): JSX.Element {
  return (
    <tr className="border-b border-foreground/5 text-sm text-foreground/85">
      <td className="p-2" />
      <td className="px-3 py-2 font-mono text-xs">
        <span className="inline-flex items-center gap-1">
          {item.workfrontLink ? (
            <a
              href={item.workfrontLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 dark:text-blue-300 underline hover:text-blue-800 dark:hover:text-blue-200"
            >
              {item.workfrontId}
            </a>
          ) : (
            item.workfrontId
          )}
          <RowActions
            value={item.workfrontId}
            onSearch={onSearch}
            onCopy={onCopy}
            copied={copiedKey === String(item.workfrontId)}
          />
        </span>
      </td>
      <td className="px-3 py-2">{item.workfrontName ?? '—'}</td>
      <td className="px-3 py-2">{item.fusionModuleName || '—'}</td>
      <td className="px-3 py-2 text-right">
        {scenarioUrl ? (
          <a
            href={`${scenarioUrl}?moduleId=${item.fusionModuleId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex items-center gap-1 font-mono text-xs',
              'text-blue-700 dark:text-blue-300 underline hover:text-blue-800 dark:hover:text-blue-200',
            )}
          >
            {item.fusionModuleId}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span className="font-mono text-xs">{item.fusionModuleId}</span>
        )}
      </td>
    </tr>
  );
}

function CustomFieldRow({
  entry,
  scenarioUrl,
  onSearch,
  onCopy,
  copiedKey,
}: {
  entry: CustomFieldEntry;
  scenarioUrl: string | null | undefined;
  onSearch: ((q: string) => void) | undefined;
  onCopy: (v: string | number) => void;
  copiedKey: string | null;
}): JSX.Element {
  const single = entry.modules.length === 1 ? entry.modules[0] : null;
  return (
    <tr className="border-b border-foreground/5 text-sm text-foreground/85">
      <td className="p-2" />
      <td className="px-3 py-2 font-mono text-xs" colSpan={2}>
        <span className="inline-flex items-center gap-1">
          {entry.customField}
          <RowActions
            value={entry.customField}
            onSearch={onSearch}
            onCopy={onCopy}
            copied={copiedKey === entry.customField}
          />
        </span>
      </td>
      <td className="px-3 py-2">
        {single ? single.fusionModuleName : `${entry.modules.length} modules`}
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs">
        {single ? (
          scenarioUrl ? (
            <a
              href={`${scenarioUrl}?moduleId=${single.fusionModuleId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-300 underline hover:text-blue-800 dark:hover:text-blue-200"
            >
              {single.fusionModuleId}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            String(single.fusionModuleId)
          )
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}

function PlanningRow({
  item,
  scenarioUrl,
  onSearch,
  onCopy,
  copiedKey,
}: {
  item: PlanningObjectItem;
  scenarioUrl: string | null | undefined;
  onSearch: ((q: string) => void) | undefined;
  onCopy: (v: string | number) => void;
  copiedKey: string | null;
}): JSX.Element {
  // For Maestro records, `objectName` is the friendly label (e.g. the
  // record title) — fall back to `recordTypeName` for Record Types
  // (where objectName ≡ recordTypeName) and to a `—` placeholder
  // otherwise.
  const name = item.objectName ?? item.recordTypeName ?? '—';
  return (
    <tr className="border-b border-foreground/5 text-sm text-foreground/85">
      <td className="p-2" />
      <td className="px-3 py-2 font-mono text-xs">
        <span className="inline-flex items-center gap-1">
          {item.planningObjectId}
          <RowActions
            value={item.planningObjectId}
            onSearch={onSearch}
            onCopy={onCopy}
            copied={copiedKey === item.planningObjectId}
          />
        </span>
      </td>
      <td className="px-3 py-2">{name}</td>
      <td className="px-3 py-2">{item.fusionModuleName || '—'}</td>
      <td className="px-3 py-2 text-right">
        {item.fusionModuleId ? (
          scenarioUrl ? (
            <a
              href={`${scenarioUrl}?moduleId=${item.fusionModuleId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-xs text-blue-700 dark:text-blue-300 underline hover:text-blue-800 dark:hover:text-blue-200"
            >
              {item.fusionModuleId}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="font-mono text-xs">{item.fusionModuleId}</span>
          )
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}

function RowActions({
  value,
  onSearch,
  onCopy,
  copied,
}: {
  value: string | number;
  onSearch: ((q: string) => void) | undefined;
  onCopy: (v: string | number) => void;
  copied: boolean;
}): JSX.Element {
  return (
    <span className="ml-1 inline-flex items-center gap-0.5">
      {onSearch ? (
        <button
          type="button"
          onClick={() => onSearch(String(value))}
          className="rounded p-1 text-foreground/50 hover:bg-foreground/10 hover:text-foreground/80"
          title={`Search scenarios by ${value}`}
          aria-label={`Search scenarios by ${value}`}
        >
          <Search className="h-3 w-3" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onCopy(value)}
        className="rounded p-1 text-foreground/50 hover:bg-foreground/10 hover:text-foreground/80"
        title={`Copy ${value}`}
        aria-label={copied ? `Copied ${value}` : `Copy ${value}`}
      >
        {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      </button>
    </span>
  );
}

interface WorkfrontGroup {
  title: string;
  items: ReadonlyArray<WorkfrontObjectItem>;
}

interface PlanningTypeGroup {
  type: string;
  items: ReadonlyArray<PlanningObjectItem>;
}

/**
 * Group planning objects by their `planningObjectType` (Ws / Rt / F /
 * Rc). Order is the human reading order — Workspace, Record Type,
 * Field, Record — with anything unrecognised landing last. Drops
 * groups with zero items after the bucketing so the SubGroupHeader
 * row only renders when there's something to show under it.
 */
function groupPlanningByType(
  items: ReadonlyArray<PlanningObjectItem>,
): ReadonlyArray<PlanningTypeGroup> {
  const order = ['Ws', 'Rt', 'F', 'Rc'];
  const groups = new Map<string, PlanningObjectItem[]>();
  for (const item of items) {
    const arr = groups.get(item.planningObjectType);
    if (arr) arr.push(item);
    else groups.set(item.planningObjectType, [item]);
  }
  return [...groups.entries()]
    .map(([type, arr]) => ({ type, items: arr }))
    .sort((a, b) => {
      const ai = order.indexOf(a.type);
      const bi = order.indexOf(b.type);
      if (ai !== bi) return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
      return a.type.localeCompare(b.type);
    });
}

function groupWorkfrontObjects(
  items: ReadonlyArray<WorkfrontObjectItem>,
): ReadonlyArray<WorkfrontGroup> {
  const groups = new Map<string, WorkfrontObjectItem[]>();
  for (const item of items) {
    const title = item.workfrontTypeLabel ?? UNIDENTIFIED;
    const arr = groups.get(title);
    if (arr) arr.push(item);
    else groups.set(title, [item]);
  }
  return [...groups.entries()]
    .map(([title, arr]) => ({ title, items: arr }))
    .sort((a, b) => {
      // Unidentified group lands first — matches legacy ordering.
      if (a.title === UNIDENTIFIED) return -1;
      if (b.title === UNIDENTIFIED) return 1;
      return a.title.localeCompare(b.title);
    });
}

interface RawCustomFieldEntry {
  customField?: unknown;
  modules?: unknown;
}

/**
 * `customFieldsData` is open-shaped JSONB. The engine populates it
 * with an array of `{ customField, modules: [{ fusionModuleId,
 * fusionModuleName, fusionModuleType }] }` entries — one per detected
 * field. Older rows may have a different shape; only entries that
 * actually look like the documented format are surfaced.
 */
function extractCustomFields(cf: CustomFieldsItem | undefined): ReadonlyArray<CustomFieldEntry> {
  if (!cf || cf.customFieldsData === null || cf.customFieldsData === undefined) return [];
  const raw = cf.customFieldsData;
  if (!Array.isArray(raw)) return [];
  const out: CustomFieldEntry[] = [];
  for (const item of raw as ReadonlyArray<RawCustomFieldEntry>) {
    if (typeof item.customField !== 'string') continue;
    const modules = Array.isArray(item.modules)
      ? (item.modules as ReadonlyArray<Record<string, unknown>>)
          .map((m) => ({
            fusionModuleId: (m.fusionModuleId ?? '') as number | string,
            fusionModuleName: typeof m.fusionModuleName === 'string' ? m.fusionModuleName : '',
            fusionModuleType:
              typeof m.fusionModuleType === 'string' ? m.fusionModuleType : undefined,
          }))
          .filter((m) => m.fusionModuleId !== '' || m.fusionModuleName)
      : [];
    out.push({ customField: item.customField, modules });
  }
  return out;
}
